import { log } from './console.js';
import { lowerCaseFirstChar, isEmpty } from './utils';

/**
 * Parses data-attributes.
 * Boolean-like data-attributes values are parsed as boolean values
 * other values are parsed as string values
 *
 * @param {HTMLElement} element element containing data attributes
 * @param {string} noDataMsg warn message to be logged when no data attributes are presented
 * @returns {Object} Object containing parsed data-attributes
 */
export const getDataFromElement = (element, noDataMsg = 'missing data-attributes') => {
  const elementData = element ? { ...element.dataset } : {};

  if (isEmpty(elementData)) {
    log.warn(noDataMsg);
  }

  // parse boolean-like data-attributes as boolean
  Object.entries(elementData).forEach(([key, value]) => {
    switch (value.toLowerCase()) {
      case 'true':
        elementData[key] = true;
        break;

      case 'false':
        elementData[key] = false;
        break;

      default:
        elementData[key] = value;
        break;
    }
  });

  return elementData;
};

/**
 * Similar to `getDataFromElement` but groups parsed values into `namespaces`
 * nested properties. Namespace is a key prefix. For example value of
 * `data-options-show-modal` attribute can have `options` namespace and resulting
 * object will look like `{ options: { showModal: true } }`
 * @param {HTMLElement} element
 * @param {Array<string>} namespaces Property key prefixes that need to
 * be converted into nested property
 * @param {string} [noDataMsg] Warn message to be logged when no data
 * attributes are presented
 * @returns {Object} Element data value grouped into nested properties
 */
export function getNamespacedDataFromElement(element, namespaces, noDataMsg) {
  const elementData = getDataFromElement(element, noDataMsg);

  return Object.entries(elementData).reduce((result, [key, value]) => {
    const namespace = namespaces.find((ns) => key.startsWith(ns));

    if (namespace) {
      const keyWithoutNamespace = lowerCaseFirstChar(key.substring(namespace.length));

      if (!result[namespace]) {
        result[namespace] = {};
      }

      result[namespace][keyWithoutNamespace] = value;
    } else {
      result[key] = value;
    }

    return result;
  }, {});
}
